import { useCallback, useEffect, useMemo, useState } from 'react';
import { Upload, UploadProps, Typography, message } from 'antd';
import { useAuthorization } from 'components/Authorization';
import { ShopeeMultiAuthShop, ShopeeMultiAuthShopStatus } from 'services/api/uploads';
import {
  isValidNumberForRegion,
  parse as phoneParser,
  format as phoneFormatter,
} from 'libphonenumber-js';
import styled from '@emotion/styled';
import { UploadChangeParam } from 'antd/lib/upload';
import { T, useTranslate } from '@tolgee/react';
import { getLeYanBotHost } from 'services/api/leyanBotHost';
import { uniqWith } from 'lodash-es';
import { DeleteOutlined, LoadingOutlined } from '@ant-design/icons';
import useAuthShopUtils from 'hooks/business/authShops/useShopeeUtils';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { ReactComponent as ExcelSvg } from '../../../assets/excel.svg';

const uploadEndpoint = `${getLeYanBotHost()}/overseaim-store-home/v1/admin/auth/read_shop_info`;

const { Paragraph } = Typography;
const { Dragger: DraggerOrig } = Upload;

const DraggerIcon = styled.p`
  margin-bottom: 10px;
  color: #ccc;
`;

const ExcelIcon = styled(ExcelSvg)`
  width: 25px;
  height: 25px;
`;

const UploadText = styled.p`
  font-size: 14px;
  color: var(--ant-primary-color);
`;

const UploadHint = styled.p`
  font-size: 12px;
  color: #b5b9bf;
`;

const BoxWrap = styled.div`
  display: flex;
  align-items: center;
  padding-left: 28px;
  padding-right: 22px;

  .anticon:hover {
    color: var(--ant-primary-color);
  }
`;
const BoxWrapName = styled.div`
  flex: 1;
  margin-left: 10px;
  text-align: left;

  & > .ant-typography {
    font-size: 14px;
    text-align: left;
    margin-bottom: 4px;
  }
`;
const Box = styled.div`
  width: 100%;
  height: 100%;
  background: #fafafa;
  text-align: center;
  border-radius: 2px;
  border: 1px dashed #d9d9d9;
  padding: 16px 0;
`;

const Dragger = styled(DraggerOrig)(
  ({ theme: { getAntdPrefixedClassName } }) => `
    &.${getAntdPrefixedClassName('upload-drag-hover')}{
      ${DraggerIcon}{
        color: var(--ant-primary-color);
      }
    }

    &:hover {
      background-color: var(--ant-primary-1);
    }
`,
);

const TipsWrap = styled.div`
  margin-top: 16px;
`;
const TipsTitle = styled.div`
  display: inline-block;
  padding: 6px 8px;
  background-color: #f0f2f5;
  color: var(--ant-primary-color);
  margin-bottom: 10px;
`;
const TipsDesc = styled.div`
  font-size: 12px;
  color: #84868c;
`;

type FileList = Required<UploadProps['fileList']>;

export interface MultiAuthXlsUploadProps {
  storeType: IM_API.Admin.StoreType;
  onChange: (shops: ShopeeMultiAuthShop[], data?: UploadChangeParam) => void;
}

enum TipsStatus {
  None,
  Normal,
  Quick,
}

const AUTH_HEADER = import.meta.env.VITE_AUTH_HEADER as string;
const MultiAuthXlsUpload = ({ storeType, onChange }: MultiAuthXlsUploadProps) => {
  const { token: authToken } = useAuthorization();
  const { t } = useTranslate();
  const [fileList, setFileList] = useState<FileList>([]);
  const [authShops, setAuthShops] = useState<ShopeeMultiAuthShop[]>([]);
  const { canQuickMode } = useAuthShopUtils();
  const [tipsStatus, setTipsStatus] = useState<TipsStatus>(TipsStatus.None);
  const sysLang = useSystemLanguageValue();

  useEffect(() => {
    if (authShops?.length > 0) {
      canQuickMode(authShops).then((result) => {
        if (result) {
          setTipsStatus(TipsStatus.Quick);
        } else {
          setTipsStatus(TipsStatus.Normal);
        }
      });
    } else {
      setTipsStatus(TipsStatus.None);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authShops?.length]);

  const uploaderDisplay = useMemo(() => {
    if (fileList && fileList.length > 0) {
      if (fileList[0].status === 'error') {
        return 'block';
      }
      return 'none';
    }

    return 'block';
  }, [fileList]);

  const handleChange = useCallback(
    (data: UploadChangeParam<any>) => {
      const { status } = data.file;
      let shops = [] as ShopeeMultiAuthShop[];
      if (status === 'removed') {
        return;
      }
      if (data.file.response) {
        if (data.file?.error) {
          if (data.file.response?.data?.status !== 200) {
            let errorMsg = '';
            try {
              const msgList = JSON.parse(data?.file?.response?.msg);
              if (sysLang === 'zh-CN') {
                const msg = msgList.find((m: any) => m.language === 'zh-CN');
                errorMsg = msg.msg;
              } else {
                const msg = msgList.find((m: any) => m.language === 'en-US');
                errorMsg = msg.msg;
              }
            } catch (err) {
              errorMsg = data.file.response?.msg || data.file.response?.data?.msg;
            }
            /* eslint-disable no-param-reassign */
            data.file.error = new Error(
              errorMsg || t('ViewShops.shopee.multi.auth.xls.upload.failed'),
            );
            data.file.status = 'error';
            /* eslint-enable no-param-reassign */
          }
        } else if (data.file.response.data?.length > 0) {
          shops = (data.file.response.data as ShopeeMultiAuthShop[]).map((shop) => {
            if (isValidNumberForRegion(shop.accountName, shop.region as General.UpperCaseRegion)) {
              const phoneNumber = phoneParser(
                shop.accountName,
                shop.region as General.UpperCaseRegion,
              );
              return {
                ...shop,
                accountName: phoneFormatter(phoneNumber, 'E.164'),
              };
            }
            return shop;
          });
          // 帐号去重
          shops = uniqWith(shops, (arrVal: ShopeeMultiAuthShop, othVal: ShopeeMultiAuthShop) => {
            return arrVal.region === othVal.region && arrVal.accountName === othVal.accountName;
          });
          const isAllAuth = shops.every((shop) => {
            const { authStatus } = shop;
            return (
              authStatus === ShopeeMultiAuthShopStatus.OtherOrg ||
              authStatus === ShopeeMultiAuthShopStatus.SameOrg
            );
          });
          if (isAllAuth) {
            /* eslint-disable no-param-reassign */
            data.file.error = new Error('文件中帐号已授权过，请重新上传');
            data.file.status = 'error';
            /* eslint-disable no-param-reassign */
            shops = [];
          }
        } else {
          /* eslint-disable no-param-reassign */
          data.file.error = new Error(
            t('ViewShops.shopee.multi.auth.xls.upload.error.no.shops.found'),
          );
          data.file.status = 'error';
          /* eslint-enable no-param-reassign */
        }
      }
      setAuthShops(shops);
      onChange(shops, data);
      setFileList([data.file]);
    },
    [onChange, sysLang, t],
  );

  return useMemo(() => {
    const headers = {
      [AUTH_HEADER]: `Bearer ${authToken}` as string,
    };

    let content;
    const file = fileList && fileList[0];
    if (file) {
      const { status, error } = file;

      if (status === 'error') {
        message.error(error.message || '网络错误，请重试');
      }

      if (status === 'uploading') {
        content = (
          <Box>
            <BoxWrap>
              <LoadingOutlined style={{ fontSize: 32 }} />
              <BoxWrapName>{file.name}</BoxWrapName>
            </BoxWrap>
          </Box>
        );
      } else if (file.status === 'success' || file.status === 'done') {
        content = (
          <Box>
            <BoxWrap>
              <ExcelIcon />
              <BoxWrapName>
                <Paragraph>{file.name}</Paragraph>
                <Paragraph>
                  <T
                    keyName="ViewShops.shopee.multi.interrupt.modal.auth.accounts"
                    params={{
                      count: authShops?.length,
                      strong: <strong />,
                    }}
                  />
                </Paragraph>
              </BoxWrapName>
              <DeleteOutlined
                onClick={() => {
                  setAuthShops([]);
                  onChange([]);
                  setFileList([]);
                }}
              />
            </BoxWrap>
          </Box>
        );
      }
    }

    return (
      <>
        <Dragger
          name="file"
          accept=".xlsx,.xls"
          style={{
            display: uploaderDisplay,
          }}
          maxCount={1}
          showUploadList={false}
          data={{ storeType }}
          action={uploadEndpoint}
          headers={headers}
          fileList={fileList}
          onChange={handleChange}
          onRemove={(file) => {
            if (file) {
              // eslint-disable-next-line no-param-reassign
              file.status = 'removed';
            }
            setFileList(fileList?.filter((f) => f.status === 'removed'));
          }}
        >
          <DraggerIcon>
            <ExcelIcon />
          </DraggerIcon>
          <UploadText>{t('ViewShops.shopee.multi.auth.xls.upload.text')}</UploadText>
          <UploadHint>{t('ViewShops.shopee.multi.auth.xls.upload.hint')}</UploadHint>
        </Dragger>
        {content}
        {tipsStatus === TipsStatus.Quick ? (
          <TipsWrap>
            <TipsTitle>{t('ViewShops.shopee.multi.auth.xls.upload.tips.title.quick')}</TipsTitle>
            <TipsDesc>{t('ViewShops.shopee.multi.auth.xls.upload.tips.desc.quick')}</TipsDesc>
          </TipsWrap>
        ) : null}
        {tipsStatus === TipsStatus.Normal ? (
          <TipsWrap>
            <TipsTitle>{t('ViewShops.shopee.multi.auth.xls.upload.tips.title.normal')}</TipsTitle>
            <TipsDesc>{t('ViewShops.shopee.multi.auth.xls.upload.tips.desc.normal')}</TipsDesc>
          </TipsWrap>
        ) : null}
      </>
    );
  }, [
    authShops?.length,
    authToken,
    fileList,
    handleChange,
    onChange,
    storeType,
    tipsStatus,
    uploaderDisplay,
    t,
  ]);
};

export default MultiAuthXlsUpload;
